import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AppProvider, useApp } from "./context/AppContext";
import { Spinner } from "./components/ui";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import AppLayout from "./components/AppLayout";
import Dashboard from "./pages/Dashboard";
import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import BillingSuccess from "./pages/BillingSuccess";
import EntityPage from "./pages/EntityPage";
import SpacePage from "./pages/SpacePage";
import GoalPage from "./pages/GoalPage";
import ProjectPage from "./pages/ProjectPage";
import Contacts from "./pages/Contacts";
import Documents from "./pages/Documents";
import OKR from "./pages/OKR";
import Inbox from "./pages/Inbox";
import Advisory from "./pages/Advisory";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import AcceptInvite from "./pages/AcceptInvite";
import Privacy from "./pages/legal/Privacy";
import Terms from "./pages/legal/Terms";
import LegalNotice from "./pages/legal/LegalNotice";

function LoadingScreen() {
  return (
    <div className="h-screen w-full flex items-center justify-center bg-background">
      <Spinner className="w-8 h-8 text-text-secondary" />
    </div>
  );
}

function Protected() {
  const { user, authLoading } = useApp();
  if (authLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

// Root path: public Landing when logged out, the app dashboard (in its shell) when logged in.
function RootIndex() {
  const { user, authLoading } = useApp();
  if (authLoading) return <LoadingScreen />;
  if (!user) return <Landing />;
  return <AppLayout><Dashboard /></AppLayout>;
}

function Router() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/google" element={<AuthCallback />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/legal" element={<LegalNotice />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/" element={<RootIndex />} />
      <Route element={<Protected />}>
        <Route element={<AppLayout />}>
          <Route path="/admin" element={<Admin />} />
          <Route path="/entity/:key" element={<EntityPage />} />
          <Route path="/entity/:key/space/:spaceId" element={<SpacePage />} />
          <Route path="/entity/:key/space/:spaceId/goal/:goalId" element={<GoalPage />} />
          <Route path="/entity/:key/space/:spaceId/project/:projectId" element={<ProjectPage />} />
          <Route path="/okr" element={<OKR />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/advisory" element={<Advisory />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/documents" element={<Documents />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Router />
      </BrowserRouter>
    </AppProvider>
  );
}
